import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import { v4 as uuidv4 } from "uuid";
import { DataContext } from "../../../context/DataContext";
import env from "../../../config/env";

/* =========================================================
   Utilidades de red con timeout + reintentos (anti-flicker)
   ========================================================= */

const NET_TIMEOUT_MS = 5000;
const RETRIES = [500, 1000, 2000]; // backoff exponencial simple

function delay(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

async function safeFetch(url, options = {}) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), NET_TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(t);
  }
}

async function fetchWithRetry(url, options = {}) {
  let lastErr;
  for (let i = 0; i <= RETRIES.length; i++) {
    try {
      const res = await safeFetch(url, options);
      if (!res.ok) {
        let bodyText = "";
        try {
          bodyText = await res.text();
        } catch (e) {
          bodyText = "";
        }
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        err.body = bodyText;
        try {
          err.data = bodyText ? JSON.parse(bodyText) : {};
        } catch (e) {
          err.data = {};
        }
        throw err;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (i < RETRIES.length) await delay(RETRIES[i]);
    }
  }
  throw lastErr;
}

/* =========================================================
   Endpoints del muro
   - GET  {BASE}/posts?limit=5&cursor=...
   - POST {BASE}/posts   { user_id, text }
   - GET  {BASE}/posts/{id}/comments
   - POST {BASE}/posts/{id}/comments { text }
   - POST {BASE}/posts/{id}/reactions { type }
   - POST {BASE}/posts/{id}/favorite { action }
   - GET  {BASE}/favorites
   ========================================================= */

// Permite configurar la base del muro vía .env (REACT_APP_API_URL),
// normalizando trailing slash y apuntando al recurso /wall por defecto.
const DEFAULT_WALL_BASE = "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/wall";
const getWallBaseUrl = () => {
  const base = env.WALL_API_URL?.replace(/\/$/, "");
  const hasWallSegment = /(?:^|\/)wall(?:$|\/|\?)/i.test(base);
  return DEFAULT_WALL_BASE 
};
const DEFAULT_CHAT_BASE = "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/chat";
const getChatBaseUrl = () => (env.CHAT_URL || env.API_URL || DEFAULT_CHAT_BASE || "").replace(/\/$/, "");
const getWallWsUrl = () => (env.WALL_WS_URL || "").replace(/\/$/, "");

const MOOD_OPTIONS = [
  { key: "happy", label: "😊", text: "Alegre" },
  { key: "neutral", label: "😐", text: "Neutral" },
  { key: "sad", label: "😞", text: "Triste" },
  { key: "angry", label: "😡", text: "Molesto" },
  { key: "anxious", label: "😨", text: "Ansioso" }
];

const POST_TYPE_OPTIONS = [
  { key: "logro", text: "Logro" },
  { key: "agradecimiento", text: "Agradecimiento" },
  { key: "duda", text: "Duda" },
  { key: "meta", text: "Meta" }
];

const POSITIVE_EMOJIS = ["😊", "🙏", "❤️", "💪", "🤗", "🌟", "✨"];

// Auth + headers (incluye x-api-key si está configurada)
const buildHeaders = (token) => {
  const headers = {};
  if (env.API_KEY) headers["x-api-key"] = env.API_KEY;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const accessToken = tokens?.accessToken?.toString();
    const idToken = tokens?.idToken?.toString();
    // Preferimos idToken para el authorizer de API Gateway (Cognito)
    //if (accessToken) return { Authorization: `Bearer ${accessToken}` };
    if (idToken) return { Authorization: `Bearer ${idToken}` };
  } catch (error) {
    console.error("EmotionWall.getAuthHeader: unable to fetch session", error);
  }
  return {};
}

async function fetchWithAuth(input, init = {}) {
  const authHeader = await getAuthHeader();

  //console.log("EmotionWall.fetchWithAuth: authHeader =", authHeader);
  const headers = {
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...buildHeaders(),
    ...authHeader,
    ...(init.headers || {})
  };

  const response = await fetchWithRetry(input, {
    ...init,
    headers,
    mode: "cors",
    cache: "no-cache"
  });

  if (response.status === 204) return null;
  if (response.status === 401) {
    const err = new Error("HTTP 401");
    err.status = 401;
    throw err;
  }
  if (response.headers.get("content-type")?.includes("application/json")) {
    return response.json();
  }
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}

async function listPosts({ limit = 5, cursor = null, token, mood, postType, topic, q, userId } = {}) {
  const base = getWallBaseUrl();
  //console.log("*** EmotionWall.listPosts: base URL =", base);
  const url = new URL(`${base}/posts`);
  url.searchParams.set("limit", String(limit));
  if (cursor) url.searchParams.set("cursor", cursor);
  if (mood) url.searchParams.set("mood", mood);
  if (postType) url.searchParams.set("post_type", postType);
  if (topic) url.searchParams.set("topic", topic);
  if (q) url.searchParams.set("q", q);
  if (userId) url.searchParams.set("user_id", userId);

  // Nota: no pasamos headers manuales para no sobreescribir el idToken que fetchWithAuth ya agrega.
  return fetchWithAuth(url.toString(), { method: "GET" }); // { items: Post[], next_cursor?: string }
}

async function listMyPosts({ limit = 5, cursor = null, mood, postType, topic, q } = {}) {
  const base = getWallBaseUrl();
  const url = new URL(`${base}/me/posts`);
  url.searchParams.set("limit", String(limit));
  if (cursor) url.searchParams.set("cursor", cursor);
  if (mood) url.searchParams.set("mood", mood);
  if (postType) url.searchParams.set("post_type", postType);
  if (topic) url.searchParams.set("topic", topic);
  if (q) url.searchParams.set("q", q);

  return fetchWithAuth(url.toString(), { method: "GET" });
}

async function createPost({ text, userName, userAvatar, token, mood, postType, topics, visibility }) {
  const base = getWallBaseUrl();
  const body = {
    method: "POST",
    body: JSON.stringify({
      text,
      ...(userName ? { user_name: userName } : {}),
      ...(userAvatar ? { user_avatar_url: userAvatar } : {}),
      ...(mood ? { mood } : {}),
      ...(postType ? { post_type: postType } : {}),
      ...(topics?.length ? { topics } : {}),
      ...(visibility ? { visibility } : {})
    })
  };
  console.log("**** EmotionWall.createPost: body =", body);

  // Igual que listPosts: dejamos que fetchWithAuth agregue el idToken en Authorization.
  return fetchWithAuth(`${base}/posts`, body); // post creado
}

async function listComments({ postId }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/comments`, { method: "GET" });
}

async function createComment({ postId, text, userName, userAvatar }) {
  const base = getWallBaseUrl();
  const body = {
    method: "POST",
    body: JSON.stringify({
      text,
      ...(userName ? { user_name: userName } : {}),
      ...(userAvatar ? { user_avatar_url: userAvatar } : {})
    })
  };
  return fetchWithAuth(`${base}/posts/${postId}/comments`, body);
}

async function createReply({ postId, commentId, text, userName, userAvatar }) {
  const base = getWallBaseUrl();
  const body = {
    method: "POST",
    body: JSON.stringify({
      text,
      ...(userName ? { user_name: userName } : {}),
      ...(userAvatar ? { user_avatar_url: userAvatar } : {})
    })
  };
  return fetchWithAuth(`${base}/posts/${postId}/comments/${commentId}/replies`, body);
}

async function updateComment({ postId, commentId, text }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/comments/${commentId}/update`, {
    method: "PUT",
    body: JSON.stringify({ text })
  });
}

async function deleteCommentApi({ postId, commentId }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/comments/${commentId}/delete`, {
    method: "DELETE"
  });
}

async function reactToPost({ postId, type }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/reactions`, {
    method: "POST",
    body: JSON.stringify({ type })
  });
}

async function toggleFavorite({ postId, action = "save" }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}/favorite`, {
    method: "POST",
    body: JSON.stringify({ action })
  });
}

async function listFavorites() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/favorites`, { method: "GET" });
}

async function deletePostApi({ postId }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/posts/${postId}`, { method: "DELETE" });
}

async function listBadges() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/badges`, { method: "GET" });
}

async function describeBadge(badgeId, context = {}) {
  const base = getChatBaseUrl();
  return fetchWithAuth(`${base}/badge/describe`, {
    method: "POST",
    body: JSON.stringify({ badge_id: badgeId, context })
  });
}

async function getStreak() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/streak`, { method: "GET" });
}

async function setHideStreak(hide) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/streak/hide`, {
    method: "POST",
    body: JSON.stringify({ hide })
  });
}

async function getProfile() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/user/profile`, { method: "GET" });
}

async function getUserProfile(userId) {
  const base = getWallBaseUrl();
  try {
    return await fetchWithAuth(`${base}/users/${userId}/profile`, { method: "GET" });
  } catch (err) {
    // Si el perfil no existe o devuelve 404, regresamos null para evitar spam en consola
    if (err?.message?.includes("404")) return null;
    return null;
  }
}

async function listSupportRequests() {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/support/requests`, { method: "GET" });
}

async function getMediaUrl(key) {
  const base = getWallBaseUrl();
  const url = new URL(`${base}/media`);
  url.searchParams.set("key", key);
  return fetchWithAuth(url.toString(), { method: "GET" });
}

async function updateWallVisibilityApi(visibility) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/user/profile/visibility`, {
    method: "POST",
    body: JSON.stringify({ visibility })
  });
}

async function updateAvailabilityApi(state) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/support/availability`, {
    method: "POST",
    body: JSON.stringify({ state })
  });
}

async function updateGoalTextApi(goalText) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/user/profile/goal`, {
    method: "POST",
    body: JSON.stringify({ goal_text: goalText })
  });
}

async function suggestGoalText() {
  const base = getWallBaseUrl();
  const res = await fetchWithAuth(`${base}/user/profile/goal/suggest`, { method: "POST" });
  return res?.suggestion || "";
}

async function requestMediaUpload({ type, contentType, filename }) {
  const base = getWallBaseUrl();
  return fetchWithAuth(`${base}/user/profile/${type}`, {
    method: "POST",
    body: JSON.stringify({ content_type: contentType, filename })
  });
}

async function updatePost({ id, text, mood, postType, topics }) {
  const base = getWallBaseUrl();
  const body = {
    method: "PUT",
    body: JSON.stringify({
      ...(text ? { text } : {}),
      ...(mood !== undefined ? { mood } : {}),
      ...(postType !== undefined ? { post_type: postType } : {}),
      ...(Array.isArray(topics) && topics.length ? { topics } : {})
    })
  };
  return fetchWithAuth(`${base}/posts/${id}`, body);
}

const normalizePost = (post = {}) => {
  const counts = post.reactions_counts || {};
  return {
    ...post,
    post_type: post.post_type || "",
    topics: post.topics || [],
    reactions_counts: {
      admire: counts.admire || 0,
      understand: counts.understand || 0,
      hug: counts.hug || 0,
      inspire: counts.inspire || 0
    },
    user_reaction: post.user_reaction || null
  };
};

const parseTopics = (str = "") =>
  str
    .split(/[,#]/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

const decodeUnicode = (str = "") =>
  str.replace(/\\u[\dA-Fa-f]{4}/g, (m) =>
    String.fromCharCode(parseInt(m.replace("\\u", ""), 16))
  );

const fixMojibake = (str = "") => {
  if (!str) return str;
  if (!/Ã|Â/.test(str)) return str;
  try {
    // Reinterpreta como latin1 -> utf8
    return decodeURIComponent(escape(str));
  } catch (e) {
    return str;
  }
};

const normalizeBotText = (text = "") => {
  if (!text) return "";
  let raw = text;
  // Intenta parsear si viene como JSON {"body": "..."} o {"response": "..."}
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      raw = parsed.body || parsed.response || text;
    }
  } catch (e) {
    // no-op si no es JSON
  }
  try {
    raw = decodeUnicode(raw);
  } catch (e) {
    // no-op
  }
  raw = fixMojibake(raw);
  return raw;
};

/* =========================================================
   Componentes UI
   ========================================================= */

function Avatar({ name = "", src = "", onError }) {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "U";
  if (src) {
    return (
      <img
        src={src}
        alt={name || "Avatar"}
        className="w-10 h-10 rounded-full object-cover"
        onError={onError}
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-semibold">
      {initials}
    </div>
  );
}

function AvatarSmart({ name = "", src = "", avatarKey, getUrl }) {
  const [url, setUrl] = useState(src || "");

  useEffect(() => {
    let active = true;
    const fetchUrl = async () => {
      if (url || !avatarKey || !getUrl) return;
      try {
        const u = await getUrl(avatarKey);
        if (active && u) setUrl(u);
      } catch (e) {
        console.error("AvatarSmart: unable to resolve avatar", e);
      }
    };
    fetchUrl();
    return () => {
      active = false;
    };
  }, [avatarKey, getUrl, url]);

  const handleError = async () => {
    try {
      const refreshed = await getUrl?.(avatarKey, true);
      setUrl(refreshed || "");
    } catch (e) {
      console.error("AvatarSmart onError refresh", e);
      setUrl("");
    }
  };

  return <Avatar name={name} src={url} onError={handleError} />;
}

function Modal({ open, onClose, children, title = "" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ComposerCard({ userInfo, onOpen }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <Avatar name={userInfo?.name} src={userInfo?.picture} />
        <button
          onClick={onOpen}
          className="flex-1 text-left px-4 py-3 bg-gray-100 rounded-full hover:bg-gray-200 transition"
        >
          <span className="text-gray-600">¿Qué estás sintiendo?</span>
        </button>
      </div>
    </div>
  );
}

function PostCard({
  post,
  onComment,
  onToggleComments,
  onReact,
  onToggleFavorite,
  onEdit,
  onDelete,
  comments = [],
  commentsLoading = false,
  commentsVisible = false,
  reactionCounts = {},
  userReaction = null,
  isFavorite = false,
  currentUserAvatar,
  userId,
  editingComment,
  editCommentText,
  onEditStart = () => {},
  onEditCancel = () => {},
  onEditChange = () => {},
  onEditSave = () => {},
  onDeleteComment = () => {},
  onReplyText = () => "",
  onReplyChange = () => {},
  onSendReply = () => {},
  onToggleReply = () => {},
  replyingTo = null,
  resolveAvatar,
  onAvatarClick = () => {},
  onAvatarHover = () => {},
  onAvatarLeave = () => {}
}) {
  const created =
    post?.created_at ? new Date(post.created_at) : new Date(); // fallback
  const name = post?.user_name || "Usuario";
  const isOwner = post?.user_id === userId;
  const avatarSrc = post?.user_avatar_url || (isOwner ? currentUserAvatar : "");
  const mood = post?.mood;
  const moodMeta = MOOD_OPTIONS.find((m) => m.key === mood);
  const postType = post?.post_type;
  const topics = post?.topics || [];

  const handleReactClick = (type, evt) => {
    const btn = evt?.currentTarget;
    if (btn) {
      btn.classList.remove("spark");
      // fuerza reflow para reiniciar la animación
      // eslint-disable-next-line no-unused-expressions
      btn.offsetWidth;
      btn.classList.add("spark");
      setTimeout(() => btn.classList.remove("spark"), 700);
    }
    onReact?.(post, type);
  };

  return (
    <article className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
      <header className="flex flex-wrap items-center gap-3 mb-2">
        <div
          role="button"
          tabIndex={0}
          className="flex-shrink-0 cursor-pointer focus:outline-none"
          onClick={() => onAvatarClick?.(post?.user_id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onAvatarClick?.(post?.user_id);
            }
          }}
          onMouseEnter={(e) => onAvatarHover?.(post, e)}
          onMouseLeave={() => onAvatarLeave?.()}
          title="Ver perfil"
        >
          <AvatarSmart name={name} src={avatarSrc} avatarKey={post?.user_avatar_key} getUrl={resolveAvatar} />
        </div>
        <div className="flex flex-col">
          <span className="font-semibold leading-tight">{name}</span>
          <span className="text-xs text-gray-500">
            {created.toLocaleString()}
          </span>
        </div>
        {moodMeta && (
          <span className="ml-auto px-3 py-1 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200">
            {moodMeta.label} {moodMeta.text}
          </span>
        )}
        {postType && (
          <span className="ml-2 px-3 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
            {postType}
          </span>
        )}
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1 ml-2">
            {topics.map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {onEdit && (
            <button
              className="text-gray-400 hover:text-gray-700"
              onClick={() => onEdit(post)}
              title="Editar publicación"
            >
              ✏️
            </button>
          )}
          {onDelete && (
            <button
              className="text-gray-400 hover:text-red-500"
              onClick={() => onDelete(post)}
              title="Eliminar publicación"
            >
              🗑️
            </button>
          )}
        </div>
      </header>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {moodMeta && (
          <span className="px-3 py-1 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200">
            {moodMeta.label} {moodMeta.text}
          </span>
        )}
        {postType && (
          <span className="px-3 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
            {postType}
          </span>
        )}
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {topics.map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
                  <p className="text-gray-800 whitespace-pre-wrap break-words">{post?.text}</p>

      <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-3 text-sm text-gray-600">
        {[
          { key: "admire", label: "Te admiro", icon: "💪" },
          { key: "understand", label: "Te entiendo", icon: "🤝" },
          { key: "hug", label: "Te abrazo", icon: "❤️" },
          { key: "inspire", label: "Me inspira", icon: "🌟" }
        ].map((r) => (
          <button
            key={r.key}
            className={`px-3 py-1 rounded-full border text-sm transition ${
              userReaction === r.key
                ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                : "border-gray-200 hover:bg-gray-100"
            }`}
            onClick={(e) => handleReactClick(r.key, e)}
            title={r.label}
          >
            <span className="text-lg" aria-hidden="true">{r.icon}</span>
            {reactionCounts?.[r.key] ? (
              <span className="ml-1 text-xs text-gray-500">({reactionCounts[r.key]})</span>
            ) : null}
          </button>
        ))}

        <div className="flex items-center gap-2 ml-2">
          <button className="hover:text-gray-700" onClick={() => onComment?.(post)}>
            💬 Comentar
          </button>
          <span className="text-gray-300">•</span>
          <button className="hover:text-gray-700" onClick={() => onToggleComments?.(post)}>
            {commentsVisible ? "Ocultar comentarios" : `${post?.comments_count || 0} comentarios`}
          </button>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <button
            className={`px-3 py-1 rounded-full border text-sm ${
              isFavorite ? "bg-amber-50 border-amber-300 text-amber-700" : "border-gray-200 hover:bg-gray-100"
            }`}
            onClick={() => onToggleFavorite?.(post)}
          >
            {isFavorite ? "★ Guardado" : "☆ Guardar"}
          </button>
        </div>
      </div>

      {/* Lista de comentarios visible solo al activar */}
      {commentsVisible && (
        <div className="mt-3">
          {commentsLoading && (
            <p className="text-sm text-gray-500">Cargando comentarios…</p>
          )}
          {!commentsLoading && comments?.length === 0 && (
            <p className="text-sm text-gray-500">Sé el primero en comentar</p>
          )}
          {!commentsLoading && comments?.length > 0 && (
            <ul className="space-y-2">
              {comments.map((c) => {
                const isBotComment =
                  (c.user_id && c.user_id.toLowerCase() === "bot") ||
                  (c.user_name || "").toLowerCase().includes("sincronybot");
                return (
                  <li key={c.id} className="rounded-lg bg-gray-50 p-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                    <AvatarSmart
                      name={c.user_name || "Usuario"}
                      src={c.user_avatar_url}
                      avatarKey={c.user_avatar_key}
                      getUrl={resolveAvatar}
                    />
                    <span className="font-semibold">{c.user_name || "Usuario"}</span>
                    <span className="text-xs text-gray-500">
                      {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                      {c.modified_at ? ` · Modificado ${new Date(c.modified_at).toLocaleString()}` : ""}
                    </span>
                    {c.user_id === userId && !isBotComment && (
                      <div className="flex gap-2 ml-auto text-xs">
                        <button
                          className="text-indigo-600 hover:underline"
                          title="Editar comentario"
                          onClick={() => onEditStart(c)}
                        >
                          ✏️
                        </button>
                        <button
                          className="text-red-500 hover:underline"
                          title="Eliminar comentario"
                          onClick={() => onDeleteComment(c)}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                    </div>
                    {editingComment?.id === c.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={editCommentText}
                          maxLength={500}
                          onChange={(e) => onEditChange(e.target.value)}
                        />
                        <div className="flex gap-2 text-sm">
                          <button
                            className="px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                            onClick={() => onEditSave(c)}
                            disabled={!editCommentText.trim()}
                          >
                            Guardar
                          </button>
                          <button
                            className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100"
                            onClick={onEditCancel}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-800 text-sm whitespace-pre-wrap">
                        {normalizeBotText(c.text)}
                      </p>
                    )}
                    {Array.isArray(c.replies) && c.replies.length > 0 && (
                      <ul className="mt-2 ml-4 space-y-1 border-l border-gray-200 pl-3">
                        {c.replies.map((r) => (
                          <li key={r.id} className="bg-white rounded-lg p-2">
                            <div className="flex items-center gap-2 text-xs text-gray-700">
                              <AvatarSmart
                                name={r.user_name || "Usuario"}
                                src={r.user_avatar_url}
                                avatarKey={r.user_avatar_key}
                                getUrl={resolveAvatar}
                              />
                              <span className="font-semibold">{r.user_name || "Usuario"}</span>
                              <span className="text-[11px] text-gray-500">
                                {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                              </span>
                            </div>
                            <p className="text-gray-800 text-sm whitespace-pre-wrap">
                              {normalizeBotText(r.text)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2">
                      {!replyingTo || replyingTo !== c.id ? (
                        <button
                          className="text-sm text-emerald-700 hover:underline"
                          onClick={() => onToggleReply(c.id)}
                        >
                          Responder
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                            placeholder="Responder con algo amable..."
                            value={onReplyText(c.id)}
                            onChange={(e) => onReplyChange(c.id, e.target.value)}
                          />
                          <div
                            role="button"
                            tabIndex={0}
                            className="px-2 py-1 rounded-lg text-emerald-700 hover:text-emerald-800 flex items-center justify-center cursor-pointer select-none"
                            onClick={() => onSendReply(c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSendReply(c);
                              }
                            }}
                            title="Responder con amabilidad"
                            aria-label="Guardar respuesta"
                          >
                            ▶️
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 animate-pulse">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-40 mb-2"></div>
          <div className="h-2.5 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-5/6"></div>
    </div>
  );
}

/* =========================================================
   Página: EmotionWall
   ========================================================= */

const EmotionWall = ({ feedMode = false, viewUserId = null, hideComposer = false }) => {
  const { userData, accessToken } = useContext(DataContext);
  const navigate = useNavigate();

  // Usuario
  const [userInfo, setUserInfo] = useState({});
  const [userId, setUserId] = useState(""); // uuid o sub de Cognito

  // Dialogo para publicar
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [postText, setPostText] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [postMood, setPostMood] = useState("");
  const [postType, setPostType] = useState("");
  const [postTopics, setPostTopics] = useState(""); // separado por comas o espacios

  // Dialogo para comentar
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [commentTarget, setCommentTarget] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [replyTexts, setReplyTexts] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [supportRequests, setSupportRequests] = useState([]);

  // Feed e infinite scroll
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Comentarios
  const [commentsByPost, setCommentsByPost] = useState({}); // {postId: Comment[]}
  const [commentsLoading, setCommentsLoading] = useState({});
  const [commentsVisible, setCommentsVisible] = useState({});

  // Favoritos
  const [favoriteIds, setFavoriteIds] = useState({}); // {postId: true}
  const [favorites, setFavorites] = useState([]);

  // Badges y racha
  const [badges, setBadges] = useState([]);
  const [badgeDescriptions, setBadgeDescriptions] = useState({});
  const [streak, setStreak] = useState({ current_streak: 0, best_streak: 0, last_30_days: 0, hide_streaks: false });
  const [profile, setProfile] = useState({ avatar_url: "", cover_url: "" });
  const avatarCacheRef = useRef({});
  const [viewProfile, setViewProfile] = useState(null);
  const [viewProfileLoaded, setViewProfileLoaded] = useState(!viewUserId);
  const [availabilityStatus, setAvailabilityStatus] = useState("available");
  const profileAttemptsRef = useRef({});
  const wsRef = useRef(null);

  // Filtro por mood
  const [moodFilter, setMoodFilter] = useState("");
  // Visibilidad del muro
  const [wallVisibility, setWallVisibility] = useState(() => localStorage.getItem("wallVisibility") || "public");
  const [postTypeFilter, setPostTypeFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Edición de post
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [editMood, setEditMood] = useState("");
  const [editType, setEditType] = useState("");
  const [editTopics, setEditTopics] = useState("");
  const [editingPost, setEditingPost] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Eliminación de post
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Media upload modals
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [coverModalOpen, setCoverModalOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [userAvatarMap, setUserAvatarMap] = useState({}); // { userId: { avatar_url, avatar_key } }

  // Errores de red/CORS
  const [netError, setNetError] = useState(null);
  // Hover card for users (solo en Inicio)
  const [hoverUserId, setHoverUserId] = useState(null);
  const [hoverProfile, setHoverProfile] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [hoverStatus, setHoverStatus] = useState(null);
  const hoverTimeoutRef = useRef(null);
  // Goal text edit
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalSuggesting, setGoalSuggesting] = useState(false);
  // Name edit
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const resolvedAvatarKeysRef = useRef({});
  const failedAvatarKeysRef = useRef({});
  const [moderationState, setModerationState] = useState({ open: false, message: "", suggestion: "", action: "" });
  const [friendlyError, setFriendlyError] = useState({ open: false, message: "" });
  const showFriendly = (setter, message) => {
    setter({ open: true, message: message || "Algo salió mal. Intenta nuevamente." });
  };
  const showFriendlyError = (msg) => setFriendlyError({ open: true, message: msg || "Algo salió mal. Intenta nuevamente." });

  const sentinelRef = useRef(null);

const resolveAvatar = async (key, force = false) => {
  if (!key) return null;
  if (failedAvatarKeysRef.current[key]) return null;
  if (!force && avatarCacheRef.current[key]) return avatarCacheRef.current[key];
  try {
    const res = await getMediaUrl(key);
    const url = res?.url || null;
    if (url) avatarCacheRef.current[key] = url;
    return url;
  } catch (e) {
    if (e?.message?.includes("401")) {
      failedAvatarKeysRef.current[key] = true; // no reintentar sin auth
    }
    console.error("resolveAvatar error", e);
    return null;
  }
};

  // Cargar info de usuario
  useEffect(() => {
    if (userData?.attributes) {
      const attrs = userData.attributes;
      setUserInfo({
        name: attrs.name || attrs.given_name || attrs.email || "Usuario",
        email: attrs.email,
        picture: attrs.picture || ""
      });
      setUserId(attrs.sub || uuidv4());
    }
  }, [userData]);

  // WebSocket en tiempo real (reacciones, etc.)
  useEffect(() => {
    const wsUrl = getWallWsUrl();
    if (!wsUrl || !userId) return;
    let ws;
    let alive = true;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Registramos la conexión con el user_id para dirigir eventos
        ws.send(JSON.stringify({ action: "init", user_id: userId }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "reaction_update" && data.payload?.post_id) {
            const { post_id, reactions_counts, user_reaction } = data.payload;
            setPosts((prev) =>
              prev.map((p) =>
                p.id === post_id
                  ? {
                      ...p,
                      reactions_counts: reactions_counts || p.reactions_counts,
                      user_reaction: user_reaction ?? p.user_reaction
                    }
                  : p
              )
            );
          }
        } catch (e) {
          console.error("ws message parse", e);
        }
      };

      ws.onclose = () => {
        if (!alive) return;
        // reconexión simple
        setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      alive = false;
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userId]);

  // Inyectar estilos para chispas (reacciones)
  useEffect(() => {
    if (document.getElementById("sparkle-style")) return;
    const style = document.createElement("style");
    style.id = "sparkle-style";
    style.textContent = `
      .spark {
        position: relative;
        overflow: hidden;
      }
      .spark::after {
        content: "✨";
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%) scale(0.2);
        opacity: 0;
        pointer-events: none;
      }
      .spark.spark::after {
        animation: sparkle-burst 0.6s ease-out;
      }
      @keyframes sparkle-burst {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.2); }
        50% { opacity: 1; transform: translate(-50%, -70%) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -90%) scale(1.4); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Suscribirse a filtros desde AppBar
  useEffect(() => {
    const handler = (e) => {
      const { search = "", mood = "", postType = "", topic = "" } = e.detail || {};
      setSearchQuery(search);
      setMoodFilter(mood);
      setPostTypeFilter(postType);
      setTopicFilter(topic);
    };
    window.addEventListener("wall-search-update", handler);
    return () => window.removeEventListener("wall-search-update", handler);
  }, []);

  // Cargar avatares de autores (una sola consulta por user_id)
  useEffect(() => {
    const unique = Array.from(new Set((posts || []).map((p) => p.user_id).filter(Boolean)));
    const pending = unique.filter((uid) => !userAvatarMap[uid] && (profileAttemptsRef.current[uid] || 0) < 2);
    if (!pending.length) return;
    let cancel = false;
    const load = async () => {
      try {
        const batch = pending.slice(0, 15); // límite defensivo
        const results = await Promise.all(
          batch.map(async (uid) => {
            try {
              const attempts = profileAttemptsRef.current[uid] || 0;
              if (attempts >= 2) return { uid, prof: null };
              const prof = await getUserProfile(uid);
              return { uid, prof };
            } catch (e) {
              profileAttemptsRef.current[uid] = (profileAttemptsRef.current[uid] || 0) + 1;
              return { uid, prof: null };
            }
          })
        );
        if (cancel) return;
        const next = {};
        results.forEach(({ uid, prof }) => {
          next[uid] = prof?.avatar_url || prof?.avatar_key ? { avatar_url: prof.avatar_url, avatar_key: prof.avatar_key } : {};
        });
        setUserAvatarMap((prev) => ({ ...prev, ...next }));
      } catch (e) {
        console.error("avatar batch load", e);
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [posts, userAvatarMap]);

  // Resolver avatar_url desde el avatar_key presente en los posts (sin pedir perfil)
  useEffect(() => {
    const tasks = [];
    (posts || []).forEach((p) => {
      const uid = p.user_id;
      const key = p.user_avatar_key;
      if (!uid || !key) return;
      const cached = userAvatarMap[uid];
      if (cached && cached.avatar_url) return;
      tasks.push({ uid, key });
    });
    if (!tasks.length) return;
    let cancel = false;
    const load = async () => {
      for (const t of tasks.slice(0, 15)) {
        if (failedAvatarKeysRef.current[t.key]) continue;
        try {
          const url = await resolveAvatar(t.key, true);
          if (cancel) return;
          if (url) {
            setUserAvatarMap((prev) => ({
              ...prev,
              [t.uid]: { ...(prev[t.uid] || {}), avatar_url: url, avatar_key: t.key }
            }));
          }
        } catch (e) {
          console.error("resolve avatar from key", t.uid, e);
        }
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [posts, userAvatarMap]);

  // Refrescar posts con avatar_url resuelto cuando falte
  useEffect(() => {
    const missing = (posts || []).filter(
      (p) => p.user_avatar_key && !p.user_avatar_url && !resolvedAvatarKeysRef.current[p.user_avatar_key]
    );
    if (!missing.length) return;
    let cancel = false;
    const load = async () => {
      for (const p of missing.slice(0, 15)) {
        if (failedAvatarKeysRef.current[p.user_avatar_key]) continue;
        try {
          const url = await resolveAvatar(p.user_avatar_key, true);
          if (cancel || !url) continue;
          resolvedAvatarKeysRef.current[p.user_avatar_key] = true;
          setPosts((prev) =>
            prev.map((it) => (it.id === p.id ? { ...it, user_avatar_url: url } : it))
          );
        } catch (e) {
          console.error("resolve avatar update", p.id, e);
        }
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [posts]);

  useEffect(() => {
    if (!viewUserId) {
      setViewProfile(null);
      setViewProfileLoaded(true);
      return;
    }
    setViewProfileLoaded(false);
    const loadViewProfile = async () => {
      try {
        const attempts = profileAttemptsRef.current[viewUserId] || 0;
        if (attempts >= 2) {
          setViewProfile({});
          return;
        }
        const vp = await getUserProfile(viewUserId);
        setViewProfile(vp || {});
        if (vp?.avatar_url) {
          setUserInfo((prev) => ({ ...prev, picture: prev.picture || vp.avatar_url, name: prev.name || vp.user_name }));
        }
      } catch (e) {
        console.error("loadViewProfile", e);
        profileAttemptsRef.current[viewUserId] = (profileAttemptsRef.current[viewUserId] || 0) + 1;
      } finally {
        setViewProfileLoaded(true);
      }
    };
    loadViewProfile();
  }, [viewUserId]);

  useEffect(() => {
    if (!feedMode) return;
    const loadSupport = async () => {
      try {
        const data = await listSupportRequests();
        setSupportRequests(data.items || []);
      } catch (e) {
        console.error("loadSupportRequests", e);
      }
    };
    loadSupport();
  }, [feedMode]);

  // Persistimos la preferencia de visibilidad para mantenerla entre sesiones
  useEffect(() => {
    localStorage.setItem("wallVisibility", wallVisibility);
  }, [wallVisibility]);

  const filterForViewUser = (items = []) => {
    if (!viewUserId) return items;
    return items.filter((p) => p.user_id === viewUserId);
  };

  // Carga inicial (evita flicker con initialLoaded)
  useEffect(() => {
    const fetchInitial = async () => {
      if (!feedMode && !viewUserId && !accessToken) return;
      if (viewUserId && !viewProfileLoaded) return;
      if (viewUserId && viewProfile?.can_view_full === false) {
        setPosts([]);
        setCursor(null);
        setHasMore(false);
        setInitialLoaded(true);
        return;
      }
      try {
        setIsFetching(true);
        let data;
        if (viewUserId) {
          data = await listPosts({ limit: 5, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery, userId: viewUserId });
        } else if (feedMode) {
          data = await listPosts({ limit: 5, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery });
        } else {
          data = await listMyPosts({ limit: 5, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery });
        }
        const items = filterForViewUser(data.items || []).map(normalizePost);
        setPosts(items);
        setCursor(data.next_cursor || null);
        setHasMore(Boolean(data.next_cursor));
        setNetError(null);
      } catch (e) {
        console.error(e);
        setNetError(feedMode ? "No se pudo cargar el feed" : "No se pudo cargar el muro");
      } finally {
        setIsFetching(false);
        setInitialLoaded(true);
      }
    };
    if (accessToken || feedMode || viewUserId) {
      fetchInitial();
    }
  }, [accessToken, moodFilter, postTypeFilter, topicFilter, searchQuery, feedMode, viewUserId, viewProfile?.can_view_full, viewProfileLoaded]);

  // Cargar favoritos del usuario
  useEffect(() => {
    if (!accessToken && !feedMode && !viewUserId) return;
    if (accessToken) {
      loadFavorites();
      loadBadgesAndStreak();
      loadProfile();
    }
  }, [accessToken, feedMode, viewUserId]);

  // Reset feed cuando cambian filtros
  useEffect(() => {
    setPosts([]);
    setCursor(null);
    setHasMore(true);
    setInitialLoaded(false);
  }, [moodFilter, postTypeFilter, topicFilter, searchQuery]);

  const inspirationLogro = posts.find((p) => p.post_type === "logro");
  const inspirationPhrase =
    "Respira hondo: no tienes que poder con todo hoy, basta con dar un paso.";
  const inspirationChallenge = "Escribe hoy 3 cosas buenas de ti y léelas en voz alta.";

  const handleToggleHideStreak = async () => {
    try {
      const next = !streak.hide_streaks;
      setStreak((prev) => ({ ...prev, hide_streaks: next }));
      await setHideStreak(next);
    } catch (e) {
      console.error(e);
      setStreak((prev) => ({ ...prev, hide_streaks: !prev.hide_streaks }));
    }
  };

  const handleToggleWallVisibility = async () => {
    const next = wallVisibility === "public" ? "private" : "public";
    setWallVisibility(next);
    try {
      await updateWallVisibilityApi(next);
    } catch (e) {
      console.error(e);
      setWallVisibility((prev) => (prev === "public" ? "private" : "public"));
      showFriendlyError("No se pudo actualizar la visibilidad del muro. Intenta nuevamente.");
    }
  };

  const handleUpload = async (type) => {
    const file = type === "avatar" ? avatarFile : coverFile;
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name?.split(".").pop() || file.type.split("/").pop() || "jpg";
      const presign = await requestMediaUpload({ type, contentType: file.type, filename: `${type}.${ext}` });
      const res = await fetch(presign.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload failed: ${res.status} ${txt}`);
      }
      // Recargar profile para obtener URL firmada
      await loadProfile();
      if (type === "avatar") {
        setAvatarModalOpen(false);
        setAvatarFile(null);
      } else {
        setCoverModalOpen(false);
        setCoverFile(null);
      }
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo cargar la imagen");
    } finally {
      setUploading(false);
    }
  };

  // IntersectionObserver para scroll infinito (pausado si hay error)
  useEffect(() => {
    if (!accessToken && !feedMode && !viewUserId) return;
    if (!sentinelRef.current) return;
    if (!hasMore) return;
    if (netError) return; // pausa si hubo error
    if (viewUserId && (!viewProfileLoaded || viewProfile?.can_view_full === false)) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isFetching && hasMore) {
          try {
            setIsFetching(true);
            let data;
            if (viewUserId) {
              data = await listPosts({ limit: 5, cursor, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery, userId: viewUserId });
            } else if (feedMode) {
              data = await listPosts({ limit: 5, cursor, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery });
            } else {
              data = await listMyPosts({ limit: 5, cursor, mood: moodFilter, postType: postTypeFilter, topic: topicFilter, q: searchQuery });
            }
            const items = filterForViewUser(data.items || []).map(normalizePost);
            setPosts((prev) => [...prev, ...items]);
            setCursor(data.next_cursor || null);
            setHasMore(Boolean(data.next_cursor));
            setNetError(null);
          } catch (e) {
            console.error(e);
            setNetError("No se pudieron cargar más publicaciones");
          } finally {
            setIsFetching(false);
          }
        }
      },
      { rootMargin: "200px" } // pre-carga
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [cursor, hasMore, isFetching, netError, accessToken, moodFilter, viewProfileLoaded, viewProfile?.can_view_full]);

  // Publicar (mantiene modal abierto si falla para no perder el texto)
  const canPublish = postText.trim().length > 0 && postText.length <= 1000;
  const remaining = 1000 - (postText?.length || 0);

  const handlePublish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    const openModeration = (payload) => {
      setModerationState({
        open: true,
        message: payload?.message || "Necesitamos ajustar tu mensaje para mantener el espacio seguro.",
        suggestion: payload?.suggestion || "",
        action: payload?.action || "",
      });
    };
    try {
      const payload = await createPost({
        text: postText.trim(),
        userName: userInfo?.name,
        userAvatar: userInfo?.picture,
        token: accessToken,
        mood: postMood,
        postType,
        topics: parseTopics(postTopics),
        visibility: wallVisibility
      });
      const normalized = normalizePost(payload);
      setPosts((prev) => [normalized, ...prev]); // inserta arriba
      // Si el backend devolvió respuesta automática del bot, la guardamos para mostrarla al abrir comentarios
      if (Array.isArray(payload?.comments) && payload.comments.length > 0) {
        setCommentsByPost((prev) => ({ ...prev, [normalized.id]: payload.comments }));
      }
      setPostText("");
      setPostMood("");
      setPostType("");
      setPostTopics("");
      setIsDialogOpen(false);
      setNetError(null);
    } catch (e) {
      console.error(e);
      if (e?.status === 422 && e?.data?.error === "Moderation") {
        openModeration(e.data);
      } else {
        showFriendlyError("No se pudo publicar. Intenta de nuevo o revisa tu conexión.");
      }
    } finally {
      setPublishing(false);
    }
  };

  // Reintentar carga completa manual (para el banner)
  const handleRetry = async () => {
    setNetError(null);
    try {
      setIsFetching(true);
      const data = await listMyPosts({
        limit: 5,
        mood: moodFilter,
        postType: postTypeFilter,
        topic: topicFilter,
        q: searchQuery
      });
      setPosts((data.items || []).map(normalizePost));
      setCursor(data.next_cursor || null);
      setHasMore(Boolean(data.next_cursor));
      setNetError(null);
    } catch (e) {
      console.error(e);
      setNetError("No se pudo conectar con el servicio");
    } finally {
      setIsFetching(false);
      setInitialLoaded(true);
    }
  };

  const loadFavorites = async () => {
    try {
      const data = await listFavorites();
      const ids = {};
      (data.items || []).forEach((fav) => {
        ids[fav.post_id] = true;
      });
      setFavoriteIds(ids);
      setFavorites(data.items || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadBadgesAndStreak = async () => {
    try {
      const [b, s] = await Promise.all([listBadges(), getStreak()]);
      const fetchedBadges = b.items || [];
      setBadges(fetchedBadges);
      // Completar descripciones con servicio de chat solo cuando falten
      const missing = Array.from(new Set(fetchedBadges.map((item) => item.badge_id).filter(Boolean))).filter(
        (id) => !badgeDescriptions[id]
      );
      if (missing.length > 0) {
        const descEntries = await Promise.all(
          missing.map(async (id) => {
            const context = fetchedBadges.find((bb) => bb.badge_id === id)?.context || {};
            try {
              const data = await describeBadge(id, context);
              return [id, data?.description || data?.body || ""];
            } catch (err) {
              console.error("describeBadge error", err);
              return [id, ""];
            }
          })
        );
        setBadgeDescriptions((prev) => {
          const next = { ...prev };
          descEntries.forEach(([id, desc]) => {
            if (desc) next[id] = desc;
          });
          return next;
        });
      }
      setStreak({
        current_streak: s.current_streak || 0,
        best_streak: s.best_streak || 0,
        last_30_days: s.last_30_days || 0,
        hide_streaks: !!s.hide_streaks
      });
    } catch (e) {
      console.error(e);
    }
  };

  const loadProfile = async () => {
    try {
      const data = await getProfile();
      setProfile(data || {});
      if (data?.wall_visibility) {
        setWallVisibility(data.wall_visibility);
      }
      if (data?.availability_status) {
        setAvailabilityStatus(data.availability_status);
      }
      if (data?.goal_text) {
        setGoalDraft(data.goal_text);
      }
      // Guardamos avatar en userInfo para reutilizarlo en posts y no hacer más consultas
      if (data?.avatar_url) {
        setUserInfo((prev) => ({ ...prev, picture: data.avatar_url }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Abrir modal de comentario
  const fetchComments = async (postId) => {
    if (!postId) return;
    setCommentsLoading((prev) => ({ ...prev, [postId]: true }));
    try {
      const data = await listComments({ postId });
      setCommentsByPost((prev) => ({ ...prev, [postId]: data.items || [] }));
    } catch (e) {
      console.error(e);
      setNetError("No se pudieron cargar los comentarios");
    } finally {
      setCommentsLoading((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleOpenComment = (post) => {
    setCommentTarget(post);
    setCommentText("");
    fetchComments(post?.id);
    setIsCommentDialogOpen(true);
  };

  const handleToggleComments = async (post) => {
    if (!post?.id) return;
    const nextVisible = !commentsVisible[post.id];
    setCommentsVisible((prev) => ({ ...prev, [post.id]: nextVisible }));
    if (nextVisible && commentsByPost[post.id] === undefined) {
      await fetchComments(post.id);
    }
  };

  const handleReact = async (post, type) => {
    if (!post?.id) return;
    try {
      const res = await reactToPost({ postId: post.id, type });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, reactions_counts: res.reactions_counts, user_reaction: res.user_reaction }
            : p
        )
      );
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo registrar la reacción");
    }
  };

  const handleToggleFavorite = async (post) => {
    if (!post?.id) return;
    const isFav = favoriteIds[post.id];
    try {
      if (isFav) {
        await toggleFavorite({ postId: post.id, action: "remove" });
        setFavoriteIds((prev) => {
          const next = { ...prev };
          delete next[post.id];
          return next;
        });
        setFavorites((prev) => prev.filter((f) => f.post_id !== post.id));
      } else {
        await toggleFavorite({ postId: post.id, action: "save" });
        setFavoriteIds((prev) => ({ ...prev, [post.id]: true }));
        setFavorites((prev) => [
          {
            id: `FAV-${post.id}`,
            post_id: post.id,
            snapshot: {
              text: post.text,
              user_name: post.user_name,
              user_id: post.user_id,
              created_at: post.created_at
            }
          },
          ...prev
        ]);
      }
      // Refrescar desde backend para asegurar consistencia (ayuda a Mi caja de herramientas)
      loadFavorites();
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo actualizar favorito");
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deletePostApi({ postId: pendingDelete.id });
      setPosts((prev) => prev.filter((p) => p.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo eliminar la publicación");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEdit = (post) => {
    if (!post) return;
    setEditingPost(post);
    setEditText(post.text || "");
    setEditMood(post.mood || "");
    setEditType(post.post_type || "");
    setEditTopics((post.topics || []).join(", "));
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPost || !editText.trim()) return;
    setSavingEdit(true);
    try {
      const updated = await updatePost({
        id: editingPost.id,
        text: editText.trim(),
        mood: editMood || undefined,
        postType: editType || undefined,
        topics: parseTopics(editTopics)
      });
      const norm = normalizePost(updated);
      setPosts((prev) => prev.map((p) => (p.id === norm.id ? { ...p, ...norm } : p)));
      setIsEditDialogOpen(false);
      setEditingPost(null);
      setEditText("");
      setEditMood("");
      setEditType("");
      setEditTopics("");
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo actualizar la publicación");
    } finally {
      setSavingEdit(false);
    }
  };

  // Enviar comentario al backend
  const handleSendComment = async () => {
    if (!commentText.trim() || !commentTarget) return;
    setCommenting(true);
    const openModeration = (payload) => {
      setModerationState({
        open: true,
        message: payload?.message || "Necesitamos ajustar tu mensaje para mantener el espacio seguro.",
        suggestion: payload?.suggestion || "",
        action: payload?.action || "",
      });
    };
    try {
      const newComment = await createComment({
        postId: commentTarget.id,
        text: commentText.trim(),
        userName: userInfo?.name,
        userAvatar: userInfo?.picture
      });

      setCommentsByPost((prev) => {
        const existing = prev[commentTarget.id] || [];
        return { ...prev, [commentTarget.id]: [...existing, newComment] };
      });

      setPosts((prev) =>
        prev.map((p) =>
          p.id === commentTarget.id
            ? { ...p, comments_count: (p.comments_count || 0) + 1 }
            : p
        )
      );

      setCommentText("");
      setIsCommentDialogOpen(false);
      setCommentTarget(null);
      setEditingComment(null);
    } catch (e) {
      console.error(e);
      if (e?.status === 422 && e?.data?.error === "Moderation") {
        openModeration(e.data);
      } else {
        showFriendlyError("No se pudo enviar el comentario. Intenta de nuevo.");
      }
    } finally {
      setCommenting(false);
    }
  };

  const handleSendEmoji = async (emoji) => {
    if (!emoji || !commentTarget) return;
    setCommenting(true);
    try {
      const newComment = await createComment({
        postId: commentTarget.id,
        text: emoji,
        userName: userInfo?.name,
        userAvatar: userInfo?.picture
      });
      setCommentsByPost((prev) => {
        const existing = prev[commentTarget.id] || [];
        return { ...prev, [commentTarget.id]: [...existing, newComment] };
      });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === commentTarget.id
            ? { ...p, comments_count: (p.comments_count || 0) + 1 }
            : p
        )
      );
      setIsCommentDialogOpen(false);
      setCommentText("");
      setCommentTarget(null);
      setEditingComment(null);
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo enviar el comentario. Intenta de nuevo.");
    } finally {
      setCommenting(false);
    }
  };

  const handleSendReply = async (comment) => {
    if (!comment) return;
    const text = (replyTexts[comment.id] || "").trim();
    if (!text) return;
    setCommenting(true);
    const openModeration = (payload) => {
      setModerationState({
        open: true,
        message: payload?.message || "Necesitamos ajustar tu mensaje para mantener el espacio seguro.",
        suggestion: payload?.suggestion || "",
        action: payload?.action || "",
      });
    };
    try {
      const postId = comment.post_id || commentTarget?.id || comment.postId;
      if (!postId) throw new Error("postId missing for reply");
      const newReply = await createReply({
        postId,
        commentId: comment.id,
        text,
        userName: userInfo?.name,
        userAvatar: userInfo?.picture
      });
      setCommentsByPost((prev) => {
        const list = prev[postId] || [];
        const updated = list.map((c) =>
          c.id === comment.id
            ? { ...c, replies: [...(c.replies || []), newReply] }
            : c
        );
        return { ...prev, [postId]: updated };
      });
      setReplyTexts((prev) => ({ ...prev, [comment.id]: "" }));
      setReplyingTo(null);
    } catch (e) {
      console.error(e);
      if (e?.status === 422 && e?.data?.error === "Moderation") {
        openModeration(e.data);
      } else {
        showFriendlyError("No se pudo responder. Intenta de nuevo.");
      }
    } finally {
      setCommenting(false);
    }
  };

  const handleSaveEditedComment = async (comment) => {
    if (!comment || !editCommentText.trim()) return;
    setCommenting(true);
    const openModeration = (payload) => {
      setModerationState({
        open: true,
        message: payload?.message || "Necesitamos ajustar tu mensaje para mantener el espacio seguro.",
        suggestion: payload?.suggestion || "",
        action: payload?.action || "",
      });
    };
    try {
      const fallbackPostId =
        posts.find((post) => (commentsByPost[post.id] || []).some((c) => c.id === comment.id))?.id;
      const postId = comment.post_id || commentTarget?.id || comment?.postId || fallbackPostId;
      if (!postId) {
        throw new Error("No se encontró el post asociado al comentario");
      }
      const updated = await updateComment({
        postId,
        commentId: comment.id,
        text: editCommentText.trim()
      });
      const updatedComment = updated?.comment || { ...comment, text: editCommentText.trim(), modified: true, modified_at: new Date().toISOString() };
      setCommentsByPost((prev) => {
        const list = prev[postId] || [];
        return {
          ...prev,
          [postId]: list.map((c) => (c.id === comment.id ? updatedComment : c))
        };
      });
      setEditingComment(null);
      setEditCommentText("");
    } catch (e) {
      console.error(e);
      if (e?.status === 422 && e?.data?.error === "Moderation") {
        openModeration(e.data);
      } else {
        showFriendlyError("No se pudo guardar el cambio. Intenta de nuevo.");
      }
    } finally {
      setCommenting(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!comment) return;
    setCommenting(true);
    try {
      await deleteCommentApi({ postId: comment.post_id, commentId: comment.id });
      setCommentsByPost((prev) => {
        const list = prev[comment.post_id] || [];
        return { ...prev, [comment.post_id]: list.filter((c) => c.id !== comment.id) };
      });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === comment.post_id ? { ...p, comments_count: Math.max(0, (p.comments_count || 1) - 1) } : p
        )
      );
      if (editingComment?.id === comment.id) {
        setEditingComment(null);
        setEditCommentText("");
      }
    } catch (e) {
      console.error(e);
      showFriendlyError("No se pudo eliminar el comentario");
    } finally {
      setCommenting(false);
    }
  };

  const handleAvailabilityChange = async (state) => {
    if (!state) return;
    setAvailabilityStatus(state);
    try {
      await updateAvailabilityApi(state);
    } catch (e) {
      console.error("update availability", e);
      setAvailabilityStatus((prev) => prev); // mantener estado actual
    }
  };

  const handleOpenGoalDialog = () => {
    if (viewUserId) return;
    setGoalDraft(profile.goal_text || goalDraft || "Hoy quiero enfocarme en cuidarme un poco más.");
    setGoalDialogOpen(true);
  };

  const handleSaveGoal = async () => {
    const txt = (goalDraft || "").trim();
    if (!txt) return;
    setGoalSaving(true);
    try {
      await updateGoalTextApi(txt);
      setProfile((prev) => ({ ...prev, goal_text: txt }));
      setGoalDialogOpen(false);
    } catch (e) {
      console.error("save goal", e);
      showFriendlyError("No se pudo guardar la frase");
    } finally {
      setGoalSaving(false);
    }
  };

  const handleSuggestGoal = async () => {
    setGoalSuggesting(true);
    try {
      const suggestion = await suggestGoalText(userInfo?.name);
      if (suggestion) setGoalDraft(suggestion);
    } catch (e) {
      console.error("suggest goal", e);
      showFriendlyError("No se pudo generar una frase ahora");
    } finally {
      setGoalSuggesting(false);
    }
  };

  const handleAvatarHover = async (post, evt) => {
    if (!feedMode) return;
    if (!post?.user_id) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoverUserId(post.user_id);
    setHoverPos({ x: evt.clientX, y: evt.clientY + 10 });
    setHoverStatus(null);
    try {
      const attempts = profileAttemptsRef.current[post.user_id] || 0;
      if (attempts >= 2) {
        setHoverProfile({ user_name: post.user_name });
        return;
      }
      const profileData = await getUserProfile(post.user_id);
      // Si la URL caducó y tenemos key, pedimos una nueva
      if (!profileData?.avatar_url && profileData?.avatar_key) {
        try {
          const fresh = await resolveAvatar(profileData.avatar_key, true);
          if (fresh) profileData.avatar_url = fresh;
        } catch (e) {
          console.error("hover avatar refresh", e);
        }
      }
      setHoverProfile(profileData || { user_name: post.user_name });
    } catch (e) {
      console.error("hover profile", e);
      profileAttemptsRef.current[post.user_id] = (profileAttemptsRef.current[post.user_id] || 0) + 1;
      setHoverProfile({ user_name: post.user_name });
    }
  };

  const handleAvatarLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverUserId(null);
      setHoverProfile(null);
      setHoverStatus(null);
    }, 200);
  };

  const sendSupportInvite = async (mode) => {
    if (!hoverUserId) return;
    if (hoverUserId === userId) {
      setHoverStatus("No puedes enviarte invitaciones a ti mism@.");
      return;
    }
    setHoverStatus("Enviando…");
    try {
      await fetchWithAuth(`${getWallBaseUrl()}/support/invite`, {
        method: "POST",
        body: JSON.stringify({ target_user_id: hoverUserId, mode })
      });
      setHoverStatus("Invitación enviada");
    } catch (e) {
      console.error("sendSupportInvite", e);
      setHoverStatus("No se pudo enviar la invitación");
    }
  };

  const coverUrl = viewUserId ? viewProfile?.cover_url : profile.cover_url;
  const avatarUrl = viewUserId ? viewProfile?.avatar_url : profile.avatar_url;
  const displayName = viewUserId
    ? (viewProfile?.user_name || viewProfile?.email || viewUserId)
    : (profile.user_name || userInfo?.name || userInfo?.email || "Usuario");
  const goalText =
    (viewUserId ? viewProfile?.goal_text : profile.goal_text) || "Hoy quiero enfocarme en cuidarme un poco más.";

  const handleOpenNameDialog = () => {
    setNameDraft(displayName || "");
    setNameDialogOpen(true);
  };

  const handleSaveName = async () => {
    const next = (nameDraft || "").trim();
    if (!next) return;
    try {
      setNameSaving(true);
      await fetchWithAuth(`${getWallBaseUrl()}/user/profile/name`, {
        method: "POST",
        body: JSON.stringify({ user_name: next }),
      });
      setProfile((prev) => ({ ...prev, user_name: next }));
      setUserInfo((prev) => ({ ...prev, name: next }));
      setNameDialogOpen(false);
    } catch (e) {
      console.error("save profile name", e);
      showFriendlyError("No se pudo actualizar tu nombre. Intenta nuevamente.");
    } finally {
      setNameSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="flex flex-col" style={{ minHeight: "calc(100vh - 74px)" }}>
        <main className="flex flex-col flex-1 overflow-y-auto" style={{ backgroundColor: "#F3F3F3" }}>
          {/* Header con portada y avatar (solo en muro propio; en vista ajena solo display sin acciones) */}
          {!feedMode && (
            <div className="relative">
              <div
                className="h-56 sm:h-64 w-full rounded-b-3xl overflow-hidden relative"
                style={{
                  backgroundImage: coverUrl ? `url(${coverUrl})` : "linear-gradient(135deg, #dbeafe, #f3e8ff)"
                }}
              >
                <div className="absolute inset-0 bg-indigo-900/20" />
                {!viewUserId && (
                  <button
                    className="absolute top-4 right-4 bg-white/80 hover:bg-white text-sm px-3 py-1.5 rounded-full shadow"
                    onClick={() => setCoverModalOpen(true)}
                  >
                    📷 Cambiar portada
                  </button>
                )}
              </div>
              <div className="relative flex flex-col items-center px-4 sm:px-6 pb-4">
                <div className="relative -mt-16 sm:-mt-20">
                  <div className="relative mx-auto">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName || "Avatar"}
                        className="w-32 h-32 sm:w-36 sm:h-36 rounded-full object-cover border-4 border-white shadow-xl"
                      />
                    ) : (
                      <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-white shadow-xl bg-indigo-200 flex items-center justify-center text-3xl font-semibold text-indigo-800">
                        {(displayName || "U").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    {!viewUserId && (
                      <button
                        className="absolute bottom-1 right-1 bg-white rounded-full p-2 shadow hover:shadow-md"
                        onClick={() => setAvatarModalOpen(true)}
                        title="Cambiar avatar"
                      >
                        📷
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-6 sm:mt-8 w-full flex flex-col gap-2 text-center">
                  <div className="group inline-flex items-center justify-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {displayName}
                    </h1>
                    {!viewUserId && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition text-gray-500 hover:text-indigo-600"
                        onClick={handleOpenNameDialog}
                        title="Editar nombre"
                        aria-label="Editar nombre"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                  {!viewUserId && (
                    <div className="flex items-center gap-2 flex-wrap justify-center text-sm">
                      {[
                        { key: "available", label: "🟢 Disponible para escuchar" },
                        { key: "low_energy", label: "🟡 Con poca energía" },
                        { key: "unavailable", label: "🔴 No disponible, me estoy cuidando" }
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          className={`px-3 py-1 rounded-full border text-xs transition ${
                            availabilityStatus === opt.key
                              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                              : "border-gray-200 text-gray-600 hover:bg-gray-100"
                          }`}
                          onClick={() => handleAvailabilityChange(opt.key)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="relative group text-sm text-gray-600 flex items-center gap-2 justify-center">
                    <span role="img" aria-label="heart">🌸</span>
                    {goalText}
                    {!viewUserId && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition ml-2 text-gray-500 hover:text-indigo-600"
                        onClick={handleOpenGoalDialog}
                        title="Editar objetivo diario"
                      >
                        ✏️
                      </button>
                    )}
                  </p>
                  {!streak.hide_streaks && !viewUserId && (
                    <p className="text-sm text-gray-700">
                      Racha de autocuidado: {streak.current_streak} días 🔥 (mejor: {streak.best_streak})
                    </p>
                  )}
                  {!viewUserId && (
                    <>
                      <p className="text-xs text-gray-500">
                        Avatar: usa una imagen que te haga sentir bien. Portada: elige algo que inspire paz. Evita contenido detonante.
                      </p>
                      <div className="mt-2 flex justify-center">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              wallVisibility === "public" ? "bg-indigo-600" : "bg-gray-300"
                            }`}
                            role="switch"
                            aria-checked={wallVisibility === "public"}
                            onClick={handleToggleWallVisibility}
                          >
                            <span
                              aria-hidden="true"
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                wallVisibility === "public" ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <div className="flex flex-col text-sm">
                            <span className="font-semibold text-gray-800">
                              {wallVisibility === "public" ? "Muro público" : "Muro privado"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {wallVisibility === "public"
                                ? "Cualquier usuario puede ver tus publicaciones."
                                : "Solo tus contactos pueden ver tu muro."}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {viewUserId && viewProfile?.can_view_full === false && (
                    <p className="text-sm text-amber-700 mt-2">
                      Este perfil es privado. Solo se muestra información básica.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="p-6 md:p-8 space-y-4">


            {/* Compositor */}
            {!hideComposer && viewProfile?.can_view_full !== false && (
              <ComposerCard userInfo={userInfo} onOpen={() => setIsDialogOpen(true)} />
            )}

            {/* Lo que puede inspirarte hoy */}
            <div className="bg-gradient-to-r from-indigo-50 via-white to-amber-50 border border-indigo-100 rounded-2xl p-4 mb-2 shadow-sm">
              <h2 className="text-lg font-semibold text-indigo-800 mb-2">Lo que puede inspirarte hoy</h2>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white/70 border border-indigo-100 rounded-xl p-3">
                  <p className="text-gray-600">Un logro de la comunidad</p>
                  <p className="font-semibold text-gray-800 mt-1">
                    {inspirationLogro?.text ? inspirationLogro.text : "“Hoy di un paso pequeño, pero mío.”"}
                  </p>
                </div>
                <div className="bg-white/70 border border-indigo-100 rounded-xl p-3">
                  <p className="text-gray-600">Frase positiva</p>
                  <p className="font-semibold text-gray-800 mt-1">{inspirationPhrase}</p>
                </div>
                <div className="bg-white/70 border border-indigo-100 rounded-xl p-3">
                  <p className="text-gray-600">Reto simple del día</p>
                  <p className="font-semibold text-gray-800 mt-1">{inspirationChallenge}</p>
                </div>
              </div>
            </div>

            {/* Progreso de autocuidado (privado) */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tu práctica de autocuidado</p>
                  {!streak.hide_streaks && (
                    <p className="font-semibold text-gray-800">
                      Racha actual: {streak.current_streak} días · Mejor racha: {streak.best_streak} · Últimos 30 días:{" "}
                      {streak.last_30_days || 0}
                    </p>
                  )}
                  {streak.hide_streaks && (
                    <p className="text-sm text-gray-600">Las rachas están ocultas. Siempre puedes mostrarlas de nuevo.</p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={streak.hide_streaks}
                    onChange={handleToggleHideStreak}
                  />
                  Ocultar rachas
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Cada pequeño paso cuenta. Si un día no puedes, no pasa nada: no empiezas de cero, empiezas desde la experiencia.
              </p>

              {badges.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm font-semibold text-gray-800 mb-2">Insignias que llevas</p>
                  <div className="flex flex-wrap gap-2">
                    {badges.map((b) => {
                      const badgeMeta = {
                        primer_logro: { icon: "🏆", label: "Primer logro" },
                        acompanar_10: { icon: "🤝", label: "Acompañé a otros 10 veces" },
                        racha_7: { icon: "🌟", label: "7 días de autocuidado" },
                      }[b.badge_id] || { icon: "🎖️", label: b.badge_id || "Insignia" };
                      const description = b.message || badgeDescriptions[b.badge_id] || "Reconocimiento a tu autocuidado";
                      return (
                        <div
                          key={b.id}
                          className="px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-sm text-emerald-800 flex items-center gap-2"
                          title={description}
                        >
                          <span className="text-lg" aria-hidden="true">{badgeMeta.icon}</span>
                          <div className="flex flex-col">
                            <span className="font-semibold">{badgeMeta.label}</span>
                            <span className="text-xs text-emerald-700">
                              {description}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>


            {/* Banner de error */}
            {netError && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl">
                <div className="flex items-center justify-between gap-4">
                  <span>{netError} — Puede ser un problema de conexión o CORS.</span>
                  <button
                    className="px-3 py-1 rounded-lg border border-amber-300 hover:bg-amber-100 text-sm"
                    onClick={handleRetry}
                    disabled={isFetching}
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            )}

            {/* Publicaciones */}
            <div className="space-y-4">
              {posts.map((p, idx) => (
                <PostCard
                  key={`${p.id || p.ts || p.created_at || "post"}-${idx}`}
                  post={{
                    ...p,
                    user_avatar_url:
                      userAvatarMap[p.user_id]?.avatar_url ||
                      p.user_avatar_url,
                    user_avatar_key: userAvatarMap[p.user_id]?.avatar_key || p.user_avatar_key
                  }}
                  onComment={handleOpenComment}
                  onToggleComments={handleToggleComments}
                  onReact={handleReact}
                  onToggleFavorite={handleToggleFavorite}
                  onEdit={p.user_id === userId ? handleOpenEdit : undefined}
                  onDelete={p.user_id === userId ? setPendingDelete : undefined}
                  comments={commentsByPost[p.id] || []}
                  commentsLoading={commentsLoading[p.id]}
                  commentsVisible={commentsVisible[p.id]}
                  reactionCounts={p.reactions_counts}
                  userReaction={p.user_reaction}
                  isFavorite={!!favoriteIds[p.id]}
                  currentUserAvatar={profile.avatar_url || userInfo?.picture}
                  userId={userId}
                  editingComment={editingComment}
                  editCommentText={editCommentText}
                  onEditStart={(c) => {
                    setCommentTarget(p);
                    setEditingComment(c);
                    setEditCommentText(c.text || "");
                  }}
                  onEditCancel={() => {
                    setEditingComment(null);
                    setEditCommentText("");
                  }}
                  onEditChange={(val) => setEditCommentText(val)}
                  onEditSave={handleSaveEditedComment}
                  onDeleteComment={handleDeleteComment}
                  onReplyText={(id) => replyTexts[id] || ""}
                  onReplyChange={(id, val) => setReplyTexts((prev) => ({ ...prev, [id]: val }))}
                  onSendReply={handleSendReply}
                  onToggleReply={(id) => setReplyingTo((prev) => (prev === id ? null : id))}
                  replyingTo={replyingTo}
                  resolveAvatar={resolveAvatar}
                  onAvatarClick={(uid) => navigate(`/support/user/${uid}`)}
                  onAvatarHover={handleAvatarHover}
                  onAvatarLeave={handleAvatarLeave}
                />
              ))}

              {/* Skeleton solo en la primera carga y si no hay error */}
              {!initialLoaded && isFetching && <LoadingSkeleton />}

              {/* Loader textual en cargas siguientes */}
              {initialLoaded && isFetching && (
                <p className="text-center text-sm text-gray-500 py-4">Cargando más…</p>
              )}

              <div ref={sentinelRef} />

              {!hasMore && posts.length > 0 && (
                <p className="text-center text-sm text-gray-500 py-4">No hay más publicaciones</p>
              )}
            </div>
          </div>
          {feedMode && supportRequests.length > 0 && (
            <div className="px-4 sm:px-6 lg:px-8 mt-4">
              <div className="bg-white rounded-2xl border border-indigo-200 p-4 space-y-3 shadow-sm">
                <h2 className="text-lg font-semibold text-indigo-800">Solicitudes de apoyo recientes</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {supportRequests.map((req) => (
                    <div
                      key={req.user_id}
                      className="flex items-center gap-3 p-3 border border-indigo-100 rounded-xl bg-gradient-to-r from-indigo-50 to-emerald-50 hover:shadow cursor-pointer"
                      onClick={() => navigate(`/support/user/${req.user_id}`)}
                    >
                      <AvatarSmart
                        name={req.user_name || req.user_id}
                        src={req.avatar_url}
                        avatarKey={req.avatar_key}
                        getUrl={resolveAvatar}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{req.user_name || req.user_id}</p>
                        <p className="text-sm text-gray-600">
                          Necesita: {req.support_request?.type || "apoyo"} · Estado: {req.availability_status || "available"}
                        </p>
                      </div>
                      <span className="text-lg">➡️</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

  {feedMode && hoverUserId && hoverProfile && (
    <div
      className="fixed z-50"
      style={{ top: hoverPos.y, left: hoverPos.x }}
      onMouseEnter={() => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      }}
      onMouseLeave={handleAvatarLeave}
    >
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 w-72">
        <div className="flex gap-3 items-center mb-2">
          <AvatarSmart
            name={hoverProfile?.user_name || "Usuario"}
            src={hoverProfile?.avatar_url}
            avatarKey={hoverProfile?.avatar_key}
            getUrl={resolveAvatar}
          />
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">
              {hoverProfile?.user_name || "Usuario"}
            </span>
            <span className="text-xs text-gray-500">
              {hoverProfile?.goal_text || "Apoyo con empatía y cuidado."}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-between">
          {[
            { key: "support_from_me", label: "Puedo apoyarte", icon: "🤝" },
            { key: "support_to_me", label: "Me puedes apoyar", icon: "💚" },
            { key: "mutual", label: "Apoyo mutuo", icon: "🌿" }
          ].map((btn) => (
            <div key={btn.key} className="group relative">
              <button
                className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center shadow-sm hover:bg-emerald-100"
                onClick={() => sendSupportInvite(btn.key)}
                aria-label={btn.label}
              >
                <span className="text-lg">{btn.icon}</span>
              </button>
              <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-md bg-green-100 text-green-800 text-xs px-2 py-1 opacity-0 group-hover:opacity-100 shadow-sm">
                {btn.label}
              </span>
            </div>
          ))}
          <button
            className="w-full px-3 py-2 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-sm hover:bg-indigo-50"
            onClick={() => navigate(`/support/user/${hoverUserId}`)}
          >
            Ver perfil
          </button>
          {hoverStatus && (
            <p className="text-xs text-gray-600 text-center">{hoverStatus}</p>
          )}
        </div>
      </div>
    </div>
  )}

      {/* Diálogo para publicar */}

      {/* Diálogo para publicar */}
      <Modal open={goalDialogOpen} onClose={() => setGoalDialogOpen(false)} title="Actualiza tu objetivo diario">
        <div className="space-y-3">
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm"
            rows={3}
            maxLength={200}
            value={goalDraft}
            onChange={(e) => setGoalDraft(e.target.value)}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 border border-gray-200 flex items-center justify-center hover:bg-gray-200"
              onClick={() => setGoalDialogOpen(false)}
              title="Cancelar"
              aria-label="Cancelar"
            >
              ✖️
            </button>
            <button
              className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 flex items-center justify-center hover:bg-indigo-100 disabled:opacity-50"
              onClick={handleSuggestGoal}
              disabled={goalSuggesting}
              title="Sugerir con IA"
              aria-label="Sugerir con IA"
            >
              ✨
            </button>
            <button
              className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100 disabled:opacity-50"
              onClick={handleSaveGoal}
              disabled={goalSaving || !goalDraft.trim()}
              title="Guardar"
              aria-label="Guardar"
            >
              💾
            </button>
          </div>
        </div>
      </Modal>

      {/* Diálogo para editar nombre */}
      <Modal open={nameDialogOpen} onClose={() => setNameDialogOpen(false)} title="Editar nombre">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Este nombre se mostrará en tu muro. Puedes ajustarlo cuando lo necesites.
          </p>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            maxLength={60}
            placeholder="Tu nombre"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 border border-gray-200 flex items-center justify-center hover:bg-gray-200"
              onClick={() => setNameDialogOpen(false)}
              title="Cancelar"
              aria-label="Cancelar"
              disabled={nameSaving}
            >
              ✖️
            </button>
            <button
              className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100 disabled:opacity-50"
              onClick={handleSaveName}
              title="Guardar"
              aria-label="Guardar"
              disabled={nameSaving || !nameDraft.trim()}
            >
              💾
            </button>
          </div>
        </div>
      </Modal>

      {/* Diálogo para publicar */}
      <Modal
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Comparte cómo te sientes"
      >
        <div className="flex items-start gap-3">
          <Avatar name={userInfo?.name} src={userInfo?.picture} />
          <div className="flex-1">
            <textarea
              className="w-full border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-gray-300 min-h-[140px]"
              placeholder="Escribe con libertad. Tu voz importa ✨"
              maxLength={1000} // protección de UI
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
            />
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">¿Cuál es tu estado de ánimo?</p>
              <div className="flex flex-wrap gap-2">
                {MOOD_OPTIONS.map((m) => (
                  <button
                    key={m.key}
                    className={`px-3 py-2 rounded-full border text-sm transition ${
                      postMood === m.key
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() => setPostMood(postMood === m.key ? "" : m.key)}
                    type="button"
                  >
                    <span className="mr-1">{m.label}</span> {m.text}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">Tipo de post</p>
              <div className="flex flex-wrap gap-2">
                {POST_TYPE_OPTIONS.map((t) => (
                  <button
                    key={t.key}
                    className={`px-3 py-2 rounded-full border text-sm transition ${
                      postType === t.key
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() => setPostType(postType === t.key ? "" : t.key)}
                    type="button"
                  >
                    {t.text}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">Temas (#)</p>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="#ansiedad, #autoestima"
                value={postTopics}
                onChange={(e) => setPostTopics(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Separa por coma o espacio. Se guardan en minúsculas.</p>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span
                className={`text-sm ${remaining < 0 ? "text-red-600" : "text-gray-500"}`}
                title="Máximo 1000 caracteres"
              >
                {remaining} caracteres restantes
              </span>
              <div className="flex gap-2">
                <button
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={publishing}
                >
                  Cancelar
                </button>
                <button
                  className={`px-4 py-2 rounded-lg text-white ${
                    publishing
                      ? "bg-gray-400 cursor-not-allowed"
                      : canPublish
                        ? "bg-indigo-600 hover:bg-indigo-700"
                        : "bg-indigo-300 cursor-not-allowed"
                  }`}
                  onClick={handlePublish}
                  disabled={publishing}
                >
                  {publishing ? "Publicando..." : "Publicar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Diálogo para comentar */}
      <Modal
        open={isCommentDialogOpen}
        onClose={() => setIsCommentDialogOpen(false)}
        title="Agregar un comentario"
      >
        <div className="flex items-start gap-3">
          <Avatar name={userInfo?.name} src={userInfo?.picture} />
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-2">
              Recuerda ser respetuoso, empático y no dar consejos médicos. Máximo 500 caracteres.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs text-gray-600 mr-2">Responde rápido con un emoji positivo:</span>
              {POSITIVE_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  className="px-3 py-1 rounded-full border border-gray-200 hover:bg-gray-100 text-lg"
                  type="button"
                  disabled={commenting}
                  onClick={() => handleSendEmoji(emoji)}
                  title="Enviar emoji"
                >
                  {emoji}
                </button>
              ))}
            </div>
            <textarea
              className="w-full border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-gray-300 min-h-[120px]"
              placeholder="Escribe un comentario"
              maxLength={500}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
                onClick={() => setIsCommentDialogOpen(false)}
                disabled={commenting}
              >
                Cancelar
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${
                  !commentText.trim() || commenting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
                onClick={handleSendComment}
                disabled={!commentText.trim() || commenting}
              >
                {commenting ? "Enviando..." : "Comentar"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Diálogo para editar publicación */}
      <Modal
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        title="Editar publicación"
      >
        <div className="flex items-start gap-3">
          <Avatar name={userInfo?.name} src={userInfo?.picture} />
          <div className="flex-1">
            <textarea
              className="w-full border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-gray-300 min-h-[140px]"
              placeholder="Actualiza tu mensaje"
              maxLength={1000}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">Estado de ánimo</p>
              <div className="flex flex-wrap gap-2">
                {MOOD_OPTIONS.map((m) => (
                  <button
                    key={m.key}
                    className={`px-3 py-2 rounded-full border text-sm transition ${
                      editMood === m.key
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() => setEditMood(editMood === m.key ? "" : m.key)}
                    type="button"
                  >
                    <span className="mr-1">{m.label}</span> {m.text}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">Tipo de post</p>
              <div className="flex flex-wrap gap-2">
                {POST_TYPE_OPTIONS.map((t) => (
                  <button
                    key={t.key}
                    className={`px-3 py-2 rounded-full border text-sm transition ${
                      editType === t.key
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() => setEditType(editType === t.key ? "" : t.key)}
                    type="button"
                  >
                    {t.text}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-1">Temas (#)</p>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="#ansiedad, #autoestima"
                value={editTopics}
                onChange={(e) => setEditTopics(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
                onClick={() => setIsEditDialogOpen(false)}
                disabled={savingEdit}
              >
                Cancelar
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${
                  !editText.trim() || savingEdit
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
                onClick={handleSaveEdit}
                disabled={!editText.trim() || savingEdit}
              >
                {savingEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Diálogo para eliminar publicación */}
      <Modal
        open={!!pendingDelete}
        onClose={() => (deleting ? null : setPendingDelete(null))}
        title="Eliminar publicación"
      >
        <p className="text-sm text-gray-700">
          ¿Seguro que quieres eliminar “{pendingDelete?.text?.slice(0, 80) || "esta publicación"}”? Esta acción no se puede deshacer.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            onClick={() => setPendingDelete(null)}
            disabled={deleting}
          >
            Cancelar
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-white ${deleting ? "bg-red-400" : "bg-red-500 hover:bg-red-600"}`}
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>

      {/* Modal de moderación */}
      <Modal
        open={moderationState.open}
        onClose={() => setModerationState({ open: false, message: "", suggestion: "", action: "" })}
        title="Cuidamos este espacio"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{moderationState.message}</p>
          {moderationState.suggestion && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              Sugerencia: {moderationState.suggestion}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
              onClick={() => setModerationState({ open: false, message: "", suggestion: "", action: "" })}
            >
              Entendido
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de moderación */}
      <Modal
        open={moderationState.open}
        onClose={() => setModerationState({ open: false, message: "", suggestion: "", action: "" })}
        title="Cuidamos este espacio"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{moderationState.message}</p>
          {moderationState.suggestion && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              Sugerencia: {moderationState.suggestion}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
              onClick={() => setModerationState({ open: false, message: "", suggestion: "", action: "" })}
            >
              Entendido
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de error amigable */}
      <Modal
        open={friendlyError.open}
        onClose={() => setFriendlyError({ open: false, message: "" })}
        title="Aviso"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{friendlyError.message}</p>
          <div className="flex justify-end">
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
              onClick={() => setFriendlyError({ open: false, message: "" })}
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal avatar */}
      <Modal open={avatarModalOpen} onClose={() => setAvatarModalOpen(false)} title="Actualizar avatar">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Te recomendamos usar una imagen que te haga sentir bien: puede ser tu rostro, un dibujo o un símbolo que te represente.
          </p>
          <input type="file" accept="image/*" onChange={(e) => setAvatarFile(e.target.files?.[0] || null)} />
          {avatarFile && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">Vista previa</p>
              <img src={URL.createObjectURL(avatarFile)} alt="preview" className="w-32 h-32 object-cover rounded-full border" />
            </div>
          )}
          <p className="text-xs text-gray-500">
            Evita imágenes con contenido violento, sexual o detonante. Queremos que este espacio sea seguro para todos.
          </p>
          <div className="flex justify-end gap-2">
            <button className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100" onClick={() => setAvatarModalOpen(false)} disabled={uploading}>
              Cancelar
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-white ${uploading ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700"}`}
              onClick={() => handleUpload("avatar")}
              disabled={!avatarFile || uploading}
            >
              {uploading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal portada */}
      <Modal open={coverModalOpen} onClose={() => setCoverModalOpen(false)} title="Actualizar portada">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Elige una imagen que te inspire paz o motivación: naturaleza, frases positivas, ilustraciones suaves, etc.
          </p>
          <input type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
          {coverFile && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">Vista previa</p>
              <div className="w-full h-40 rounded-xl overflow-hidden border">
                <img src={URL.createObjectURL(coverFile)} alt="preview cover" className="w-full h-full object-cover" />
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Evita imágenes con contenido violento, sexual o detonante. Queremos que este espacio sea seguro para todos.
          </p>
          <div className="flex justify-end gap-2">
            <button className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100" onClick={() => setCoverModalOpen(false)} disabled={uploading}>
              Cancelar
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-white ${uploading ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700"}`}
              onClick={() => handleUpload("cover")}
              disabled={!coverFile || uploading}
            >
              {uploading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
};

export default EmotionWall;
